import { File, Paths } from 'expo-file-system';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { Platform } from 'react-native';

import { color } from '@/constants/tokens';
import type { MinimalBooking } from '@/src/api/bookingsApi';
import { formatDateTime } from '@/src/utils/dateFormatter';

/**
 * "Download all tickets", off the delete-account flow.
 *
 * There is no ticket ARTIFACT on the server — a ticket is `GET /bookings/{id}`
 * rendered by a screen behind the token, which 401s forever once the account is
 * gone. So this manufactures one: the reservations we already hold, printed to a
 * PDF the user keeps. Everything on the page comes off `Reservation`; nothing is
 * fetched.
 *
 * No event images. A remote `<img>` would make `printToFileAsync` wait on the CDN
 * and hang the button on a bad connection, which is a poor trade for a photo.
 */

const FILE_NAME = 'putting-scene-tickets.pdf';

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

// Every value below is event- or user-supplied and goes straight into markup.
const escape = (value: string) =>
  value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);

const row = (label: string, value: string) =>
  `<tr><th>${escape(label)}</th><td>${escape(value)}</td></tr>`;

function ticketSection(reservation: MinimalBooking): string {
  const { event, slot, tickets, id } = reservation;
  const seats = tickets
    .map((ticket) => `${ticket.quantity} × ${ticket.ticketTierName}`)
    .join(', ');
  const contact = [event.support?.name, event.support?.mobile]
    .filter(Boolean)
    .join(' · ');

  return `<section>
      <h2>${escape(event.title)}</h2>
      <table>
        ${row('When', formatDateTime(slot.startDatetime))}
        ${row('Where', event.venue?.name ?? '')}
        ${row('Tickets', seats)}
        ${row('Reference', id)}
        ${contact ? row('Event contact', contact) : ''}
      </table>
    </section>`;
}

/**
 * The printable document. Exported for the sake of eyeballing it — a wrong PDF
 * is much easier to diagnose from the HTML than from the render.
 *
 * Colors come from our palette rather than hex literals so the page reads as
 * ours; they're plain CSS strings here, not `$tokens` (nothing in this file is a
 * Tamagui component).
 */
export function ticketsHtml(
  reservations: MinimalBooking[],
  fullName: string
): string {
  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      @page { margin: 32pt; }
      body { font-family: -apple-system, "Helvetica Neue", Arial, sans-serif; color: ${color.textDark}; }
      h1 { font-size: 20pt; margin: 0 0 2pt; }
      h2 { font-size: 13pt; margin: 0 0 8pt; }
      p { margin: 0 0 4pt; font-size: 10pt; color: ${color.textDarkMuted}; }
      .note { margin: 12pt 0 20pt; padding: 10pt 12pt; border-radius: 8pt; background: ${color.backgroundSecondary}; color: ${color.textDark}; font-size: 10pt; }
      section { border: 1pt solid ${color.borderColor}; border-radius: 8pt; padding: 12pt 14pt; margin-bottom: 12pt; page-break-inside: avoid; }
      table { border-collapse: collapse; width: 100%; font-size: 11pt; }
      th { width: 92pt; padding: 2pt 0; text-align: left; vertical-align: top; font-weight: 500; color: ${color.textDarkMuted}; }
      td { padding: 2pt 0; vertical-align: top; }
    </style>
  </head>
  <body>
    <h1>Your tickets</h1>
    <p>${escape(fullName)} · ${reservations.length} upcoming ${reservations.length === 1 ? 'booking' : 'bookings'}</p>
    <div class="note">
      These bookings stay valid after your Putting Scene account is deleted. Keep
      this file — once the account is gone you won't be able to open them in the
      app. Quote the reference below to the event contact if you need help.
    </div>
    ${reservations.map(ticketSection).join('\n')}
  </body>
</html>`;
}

/**
 * Render the PDF and hand it to the user. Resolves once the OS has taken it —
 * which includes the user dismissing the share sheet, since a dismissal isn't a
 * failure and there's nothing to report either way.
 *
 * Two platforms, two mechanisms: native prints to a file and shares it, web has
 * no file to hand anywhere and opens the browser's print dialog, where "Save as
 * PDF" IS the download.
 */
export async function downloadTickets(
  reservations: MinimalBooking[],
  fullName: string
): Promise<void> {
  const html = ticketsHtml(reservations, fullName);

  if (Platform.OS === 'web') {
    await Print.printAsync({ html });
    return;
  }

  const { uri } = await Print.printToFileAsync({ html });

  // `printToFileAsync` names the file after a random hash, and this one is meant
  // to be KEPT — the hash is what the user would see in Files/Drive. Rename it
  // before the share sheet ever shows it. A leftover from a previous download
  // has to go first: moving onto an existing path throws.
  const named = new File(Paths.cache, FILE_NAME);
  if (named.exists) named.delete();
  await new File(uri).move(named);

  if (await Sharing.isAvailableAsync()) {
    await Sharing.shareAsync(named.uri, {
      mimeType: 'application/pdf',
      UTI: 'com.adobe.pdf',
      dialogTitle: 'Your tickets',
    });
    return;
  }

  // No share sheet (a restricted device) — the print dialog can still save it.
  await Print.printAsync({ uri: named.uri });
}
